/** Stable line id across paList payload shapes. Prefer claim_items.id_ (also projected as claim_item_id). */
export function getPaLineItemId(item) {
  if (!item) return null;
  const id = item.claim_item_id ?? item.id_ ?? item.item_id;
  return id == null || id === '' ? null : id;
}

/**
 * PA line decision status from paList rows.
 * 0 / empty = pending, 1 = approved, 2 = queried (still actionable), 3 = rejected.
 */
export function getPaLineItemStatus(item) {
  if (!item) return 0;
  const raw = item.claim_item_status ?? item.claim_item_status_id ?? item.pa_item_status;
  if (raw == null || raw === '') return 0;
  const num = Number(raw);
  return Number.isFinite(num) ? num : 0;
}

/** Pending (0) and queried (2) lines can receive batch draft decisions. */
export function isPaLineItemActionable(item) {
  const statusNum = getPaLineItemStatus(item);
  return !statusNum || statusNum === 2;
}

/**
 * Display name for a filed PA / claim line.
 * Uses the API snapshot only — never re-resolve from item_id or costable catalog.
 */
export function paLineItemName(item) {
  if (!item) return '--';
  const name = item.item_name ?? item.filed_item_name;
  return name != null && String(name).trim() !== '' ? String(name) : '--';
}

function nonemptyPaText(value) {
  if (value == null) return '';
  const text = String(value).trim();
  if (text === '' || text === '0' || text.toLowerCase() === 'null') return '';
  return text;
}

function nonemptyPaCount(value) {
  if (value == null || value === '') return '';
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) return '';
  return String(num);
}

function pushUniquePaField(rows, label, value) {
  const text = nonemptyPaText(value);
  if (!text) return;
  const key = text.toLowerCase();
  if (rows.some((row) => row.value.toLowerCase() === key)) return;
  rows.push({ label, value: text });
}

/**
 * Compact clinical identifiers for the PA decide row (brand, strength, form, code).
 * Empty / zero catalog values are omitted. Does not invent pack size, route, or NAFDAC.
 */
export function paLineItemClinicalParts(item) {
  if (!item) return [];
  const rows = [];
  const name = paLineItemName(item);
  const brand = nonemptyPaText(item.item_brand);
  if (brand && brand.toLowerCase() !== String(name).toLowerCase()) {
    rows.push({ label: 'Brand', value: brand });
  }
  pushUniquePaField(rows, 'Strength', item.item_strength);
  pushUniquePaField(rows, 'Presentation', item.item_presentation);
  pushUniquePaField(rows, 'Dosage', item.item_dosage);
  pushUniquePaField(rows, 'Type', item.item_type);
  pushUniquePaField(rows, 'Code', item.item_code);
  return rows;
}

export function formatPaLineItemClinicalLine(item) {
  return paLineItemClinicalParts(item).map((part) => part.value).join(' · ');
}

/**
 * Labeled catalog + prescribed fields for the PA item details modal.
 * Quantity / cost stay in the Requested section.
 */
export function paLineItemDetailFields(item) {
  if (!item) return [];
  const rows = [];
  const name = paLineItemName(item);
  const brand = nonemptyPaText(item.item_brand);
  if (brand && brand.toLowerCase() !== String(name).toLowerCase()) {
    rows.push({ label: 'Brand', value: brand });
  }
  pushUniquePaField(rows, 'Strength', item.item_strength);
  pushUniquePaField(rows, 'Presentation', item.item_presentation);
  pushUniquePaField(rows, 'Dosage', item.item_dosage);
  pushUniquePaField(rows, 'Type', item.item_type);
  pushUniquePaField(rows, 'Code', item.item_code);
  const planItem = nonemptyPaText(item.plans_items_name);
  const category = nonemptyPaText(item.category_name);
  if (planItem && planItem.toLowerCase() !== category.toLowerCase()) {
    rows.push({ label: 'Plan item', value: planItem });
  }
  const dose = nonemptyPaCount(item.dosage);
  const frequency = nonemptyPaCount(item.frequency);
  const duration = nonemptyPaCount(item.duration);
  if (dose) rows.push({ label: 'Dose', value: dose });
  if (frequency) rows.push({ label: 'Frequency', value: frequency });
  if (duration) rows.push({ label: 'Duration', value: duration });
  return rows;
}

/**
 * When the PA line was filed (`claim_items.date`).
 * Multi-day encounters can add items on different days.
 */
export function getPaLineItemFiledAt(item) {
  if (!item) return null;
  const raw = item.date ?? item.filed_at ?? item.filed_date ?? item.created_at ?? item.createdAt;
  if (raw == null || raw === '') return null;
  return raw;
}

/**
 * When the PA line was decided (its Processed time), Africa/Lagos.
 * The API sends `processed_at` — the latest decision, already converted from
 * the server clock. Null while pending or queried. Only an older API without
 * that field falls back to the raw decision column.
 */
export function getPaLineItemProcessedAt(item) {
  if (!item) return null;
  const status = getPaLineItemStatus(item);
  if (status !== 1 && status !== 3) return null;
  const raw = Object.prototype.hasOwnProperty.call(item, 'processed_at')
    ? item.processed_at
    : (status === 1 ? item.pa_approved_at : item.pa_rejected_at);
  if (raw == null || raw === '' || String(raw).startsWith('0000-00-00')) return null;
  return raw;
}

/** When the facility submitted this line (its Received time). */
export function getPaLineItemReceivedAt(item) {
  if (!item) return null;
  return item.received_at || getPaLineItemFiledAt(item);
}

/**
 * Received / Processed times, in the one format every turnaround screen uses
 * (processing history, item panel): "14 Oct 2026, 18:21", 24-hour, Lagos time
 * as sent by the API.
 */
export function formatPaTurnaroundTime(value) {
  if (!value) return '--';
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!m) return String(value);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${Number(m[3])} ${months[Number(m[2]) - 1]} ${m[1]}, ${m[4]}:${m[5]}`;
}

/** Seconds → "2h 15m" / "1d 3h" / "<1m". */
export function formatPaDuration(seconds) {
  if (seconds == null || seconds === '') return '';
  const s = Math.floor(Number(seconds));
  if (!Number.isFinite(s) || s < 0) return '';
  if (s < 60) return '<1m';
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d >= 1) return h > 0 ? `${d}d ${h}h` : `${d}d`;
  if (h >= 1) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  return `${m}m`;
}

/** Received → Processed for one line. Empty while pending. */
export function formatPaLineItemTurnaround(item) {
  if (!getPaLineItemProcessedAt(item)) return '';
  if (item?.tat_seconds != null && item.tat_seconds !== '') return formatPaDuration(item.tat_seconds);
  const received = new Date(getPaLineItemReceivedAt(item) || NaN).getTime();
  const processed = new Date(getPaLineItemProcessedAt(item) || NaN).getTime();
  if (!Number.isFinite(received) || !Number.isFinite(processed) || processed < received) return '';
  return formatPaDuration((processed - received) / 1000);
}

/** Format PA timestamps consistently (e.g. Jul 20, 2026 09:44 PM). */
export function formatPaDateTime(dateValue) {
  if (!dateValue) return '--';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return String(dateValue);
  return `${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} ${date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
}

/**
 * Optional evidence block on GET /v2/pa/{id}/checkout-validation responses.
 */
export function formatCheckoutEvidenceSummary(validation) {
  if (!validation || typeof validation !== 'object') return null;

  const evidence = validation.evidence ?? validation.pa_evidence;
  if (evidence && typeof evidence === 'object') {
    const parts = [];
    if (evidence.header_required) {
      parts.push(
        evidence.header_present || evidence.header_satisfied
          ? 'Encounter document attached'
          : 'Encounter document required before checkout',
      );
    }
    const pendingLines = Number(evidence.pending_line_count ?? evidence.pending_lines ?? 0);
    if (pendingLines > 0) {
      parts.push(`${pendingLines} line(s) still need document(s)`);
    }
    if (parts.length) return parts.join(' · ');
  }

  if (validation.evidence_status) {
    return String(validation.evidence_status).replace(/_/g, ' ');
  }
  if (validation.pending_evidence === true) {
    return 'Supporting documents must be uploaded and PA finalized before checkout';
  }
  return null;
}
